import { Component, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertType, ModalComponent, ModalResult } from '@michelin/theme';
import { Store } from '@ngxs/store';
import { SpecificationType } from 'src/app/features/asset-management/models/SpecLine';
import { Specification } from 'src/app/features/asset-management/models/Specification';
import {
  CreateSpecificationPayload,
  mapToCreateSpecificationPayload
} from 'src/app/features/asset-management/models/request/CreateSpecificationPayload';
import { SpecificationResponse } from 'src/app/features/asset-management/models/response/response.model';
import { AssetManagementService } from 'src/app/features/asset-management/services/asset-management.service';
import { UpdateCurrentSpecificationAction, UpdateEditingModeAction } from 'src/app/shared/state/AssetManagementState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { DealerState } from '../../../../shared/state/DealerState';
import { specCodeValidator } from '../../utils/SpecificationUtils';
import { UtilityService } from '../../../../shared/services/utility.service';

@Component({
  selector: 'app-create-cam-spec',
  templateUrl: './create-cam-spec.component.html',
  styleUrls: ['./create-cam-spec.component.scss'],
  standalone: false
})
export class CreateCamSpecComponent {
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public fb = inject(FormBuilder);
  public assetManagementService = inject(AssetManagementService);
  public utilityService = inject(UtilityService);
  protected translationService = inject(TranslationService);
  public store = inject(Store);

  @ViewChild('createComponentModal') modal: ModalComponent;
  @ViewChild('createComponentContentModal') contentModal: TemplateRef<any>;

  camSpecificationForm: FormGroup;
  specificationLines: FormArray;
  currentSpecificationLineForm: FormGroup;

  specification: Specification;

  showCreateSpecificationForm = false;
  showDiscardButton = true;
  showContinueButton = true;
  showSaveButton = false;
  showBackButton = false;
  createMode = true;
  defaultROAdded = false;

  dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
  nafaMRT = 'NAFA';
  type = this.dealer.name === this.nafaMRT ? 'NAFA' : 'LOCAL';
  initialCode = this.dealer.name === this.nafaMRT ? 'BT' : '';

  saveProcessing = false;
  continueProcessing = false;
  step = 0;
  camSpecName = '';

  textFieldClass = 'edit-component';

  constructor() {
    this.camSpecificationForm = this.fb.group({
      specCode: [this.initialCode, [Validators.required, specCodeValidator(this.type)]],
      specDescription: ['', Validators.required],
      specType: [this.type, Validators.required],
      lines: this.fb.array([])
    });
    this.handleSpecCodeChanges();
    this.specificationLines = this.camSpecificationForm.get('lines') as FormArray;

    this.store.dispatch(new UpdateEditingModeAction(true));
    this.store.dispatch(new UpdateCurrentSpecificationAction(undefined));
  }

  continue() {
    if (this.step === 0) {
      this.camSpecificationForm.markAllAsTouched();
      if (this.camSpecificationForm.status === 'INVALID') {
        this.utilityService.showAlert(
          AlertType.strong,
          this.translationService.translate('incorrectValuesText'),
          this.translationService.translate('incorrectValuesAlertText')
        );
      } else {
        // check if cam name already exists
        this.continueProcessing = true;
        this.camSpecName = this.camSpecificationForm.get('specCode').value;
        this.assetManagementService.checkIfCamAlreadyExists(this.camSpecName).subscribe((res: boolean) => {
          if (res) {
            this.camSpecificationForm.get('specCode').setErrors({ alreadyExists: true });
            this.utilityService.showAlert(
              AlertType.strong,
              this.translationService.translate('incorrectValuesText'),
              this.translationService.translate('incorrectValuesAlertText')
            );
          } else {
            this.camSpecificationForm.get('specCode').setErrors(null);
            this.showCreateSpecificationForm = true;
            this.step++;
            this.addDefaultLine('Default RR', 'Default Repair & Retread (RR)', 0, SpecificationType.REPAIR_RETREAD);
            this.router.navigate(['line/0'], { relativeTo: this.route });
          }
          this.continueProcessing = false;
        });
      }
    } else if (this.step === 1) {
      (this.camSpecificationForm.get('lines') as FormArray).at(0).markAllAsTouched();
      if ((this.camSpecificationForm.get('lines') as FormArray).at(0).status === 'INVALID') {
        this.utilityService.showAlert(
          AlertType.strong,
          this.translationService.translate('incorrectValuesText'),
          this.translationService.translate('incorrectValuesAlertText')
        );
      } else {
        if (!this.defaultROAdded) {
          this.addDefaultLine('Default RO', 'Default Repair Only (RO)', 1, SpecificationType.REPAIR_ONLY);
          this.defaultROAdded = true;
        }
        this.showDiscardButton = true;
        this.showContinueButton = false;
        this.showBackButton = true;
        this.showSaveButton = true;
        this.step++;
        this.router.navigate(['line/1'], { relativeTo: this.route });
      }
    }
  }

  save() {
    (this.camSpecificationForm.get('lines') as FormArray).at(1).markAllAsTouched();
    if ((this.camSpecificationForm.get('lines') as FormArray).at(1).status === 'INVALID') {
      this.utilityService.showAlert(
        AlertType.strong,
        this.translationService.translate('incorrectValuesText'),
        this.translationService.translate('incorrectValuesAlertText')
      );
    } else {
      if (this.camSpecName !== this.camSpecificationForm.get('specCode').value) {
        this.assetManagementService.checkIfCamAlreadyExists(this.camSpecificationForm.get('specCode').value).subscribe((res: boolean) => {
          if (res) {
            this.camSpecificationForm.get('specCode').setErrors({ alreadyExists: true });
            this.utilityService.showAlert(
              AlertType.strong,
              this.translationService.translate('incorrectValuesText'),
              this.translationService.translate('incorrectValuesAlertText')
            );
          } else {
            this.camSpecificationForm.get('specCode').setErrors(null);
            this.createSpecification();
          }
        });
      } else {
        this.createSpecification();
      }
    }
  }

  back() {
    this.showDiscardButton = true;
    this.showBackButton = false;
    this.showSaveButton = false;
    this.showContinueButton = true;
    this.step--;
    this.router.navigate(['line/0'], { relativeTo: this.route });
  }

  discard() {
    this.showConfirmationModal('discard-confirmation', 'You have unsaved changes', 'Continue Set Up', 'Discard Changes');
  }

  showConfirmationModal(id: string, title: string, confirmText: string, cancelText: string): void {
    this.modal.open({
      id: id,
      width: 550,
      title: title,
      content: this.contentModal,
      confirm: confirmText,
      cancel: cancelText
    });
  }

  onConfirmationModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discard-confirmation' && !result.result) {
        this.router.navigate(['home/specifications']);
      } else if (result.id === 'back-confirmation' && result.result) {
        this.showDiscardButton = true;
        this.showBackButton = false;
        this.step--;
        this.router.navigate(['line/0'], { relativeTo: this.route });
      }
    }
  }

  createSpecification() {
    this.saveProcessing = true;
    let createSpecificationPayload: CreateSpecificationPayload;

    createSpecificationPayload = mapToCreateSpecificationPayload(this.camSpecificationForm, this.store, this.translationService);
    this.assetManagementService.createSpecification(createSpecificationPayload).subscribe({
      next: (res: SpecificationResponse) => {
        this.showDiscardButton = false;
        this.showContinueButton = false;
        this.showBackButton = false;
        this.showSaveButton = false;
        this.textFieldClass = 'view-component';
        this.createMode = false;

        this.assetManagementService.notifySpecificationCreated(true);
        this.store.dispatch(new UpdateEditingModeAction(false));

        this.specification = res.data;
        this.store.dispatch(new UpdateCurrentSpecificationAction(this.specification));

        this.utilityService.showAlert(
          AlertType.success,
          this.translationService.translate('camCreateSuccessTitleText'),
          this.translationService.translate('camCreateSuccessContentText')
        );
        this.saveProcessing = false;
        this.router.navigateByUrl(`home/specifications/${this.specification.id}`);
      },

      error: () => {
        this.utilityService.showAlert(
          AlertType.critical,
          this.translationService.translate('incorrectValuesText'),
          this.translationService.translate('incorrectValuesAlertText')
        );
        this.saveProcessing = false;
      }
    });
  }

  addDefaultLine(label: string, lineName: string, lineIndex: number, type: number): void {
    const speclineForm = this.createLine();
    speclineForm.patchValue({
      label,
      lineIndex,
      lineName,
      type,
      isDefault: true,
      secondRepairLimitAdded: false,
      definition: {
        applicationDefinition: 'All',
        axles: ['All'],
        sizes: ['All'],
        makes: ['All'],
        treads: ['All'],
        series: ['All']
      },
      ntr: {
        action: 'REJECT'
      },
      age: {
        action: 'REJECT'
      },
      limit0: {
        action: 'REJECT'
      }
    });
    speclineForm.get('ntr').get('disposition').addValidators(Validators.required);
    speclineForm.get('age').get('disposition').addValidators(Validators.required);
    speclineForm.get('limit0').get('disposition').addValidators(Validators.required);
    (this.camSpecificationForm.get('lines') as FormArray).push(speclineForm);
    this.currentSpecificationLineForm = speclineForm;
    this.specificationLines = this.camSpecificationForm.get('lines') as FormArray;
  }

  createLine(): FormGroup {
    return this.fb.group({
      label: [''],
      lineIndex: [0],
      lineName: [''],
      type: [0],
      isDefault: [false],
      secondRepairLimitAdded: [false],
      definition: this.utilityService.getDefinitionFormGroup(),
      ntr: this.utilityService.getActionDetailsFormGroup(),
      age: this.utilityService.getActionDetailsFormGroup(),
      limit0: this.utilityService.getLimitFormGroup(true),
      limit1: this.utilityService.getLimitFormGroup(false)
    });
  }

  specificationLink(lineIndex: number) {
    return `/home/specifications/create/line/${lineIndex}`;
  }

  disable_key_sidebar(event: any) {
    if (this.createMode === true) {
      event.preventDefault();
      return false;
    }
  }

  private handleSpecCodeChanges() {
    const specCodeControl = this.camSpecificationForm.get('specCode');
    const element = document.getElementById('specCodeInput') as HTMLInputElement;

    if (this.dealer.name === this.nafaMRT) {
      if (!specCodeControl.value) {
        specCodeControl.setValue('BT');
      } // Set initial value
      specCodeControl?.valueChanges.subscribe((value) => {
        // If value doesn't start with "BT", reset to "BT"
        if (!value.startsWith('BT')) {
          specCodeControl.setValue('BT', { emitEvent: false });
          if (element) {
            element.value = 'BT';
            element.setSelectionRange(2, 2); // Move cursor after "BT"
          }
        }
      });
    }
  }
}
